import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import ArrowUpIcon from '@mui/icons-material/ArrowUpwardOutlined'
import ArrowDownIcon from '@mui/icons-material/ArrowDownwardOutlined'
import TopoIcon from '@mui/icons-material/VerticalAlignTopOutlined'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import SolicitacaoDetalhe from './SolicitacaoDetalhe'
import { atrasada, dataDoPrazo, ordemMudou, reposicionar, Resumo, rotuloPrioridade, rotuloTipo } from './comum'
import '../gestao/gestao.scss'
import './solicitacoes.scss'

/**
 * Chamados › Minha fila: os pedidos de desenvolvimento que a gestão atribuiu a quem está logado e que esperam na fila, na ordem
 * em que ele vai atender. Ele arruma (arrastando ou pelos botões) e salva; quem pediu recebe o aviso do novo lugar.
 * A tela só pede a lista ao abrir e depois de salvar: nada que chega do servidor desfaz uma arrumação ainda não salva.
 */
export default function MinhaFila() {
  const { notify } = useNotification()
  const [params, setParams] = useSearchParams()
  const [salva, setSalva] = useState<Resumo[] | null>(null)
  const [fila, setFila] = useState<Resumo[]>([])
  const [erro, setErro] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [arrastando, setArrastando] = useState<number | null>(null)

  const aberta = Number(params.get('abrir')) || null
  const mudou = !!salva && ordemMudou(fila, salva)

  const carregar = useCallback(async () => {
    const r = await services.api.solicitacoesMinhaFila()
    if (r.fail) { setErro(r.error || 'Não foi possível carregar a sua fila.'); return }
    setErro(null)
    setSalva(r.data)
    setFila(r.data)
  }, [])

  useEffect(() => { carregar() }, [carregar])

  const levar = (id: number, destino: number) => setFila(f => reposicionar(f, id, destino))

  const salvar = async () => {
    setSalvando(true)
    const r = await services.api.solicitacoesArrumarMinhaFila(fila.map(s => s.id))
    setSalvando(false)
    if (r.fail) { notify(r.error || 'Não foi possível salvar a ordem.', { variant: 'error' }); return }
    setSalva(r.data)
    setFila(r.data)
    notify('Ordem da fila salva. Quem pediu foi avisado do novo lugar.', { variant: 'success' })
  }

  const soltar = (e: React.DragEvent, destino: number) => {
    e.preventDefault()
    const id = Number(e.dataTransfer.getData('text/plain')) || arrastando
    setArrastando(null)
    if (id) levar(id, destino)
  }

  const abrir = (id: number) => setParams({ abrir: String(id) })
  const fechar = () => { setParams({}); if (!mudou) carregar() }

  return (
    <div className="ng">
      <div className="ng-cab">
        <h1>Minha fila</h1>
        <div className="ng-acoes">
          <button type="button" className="ng-btn" disabled={!mudou || salvando} onClick={() => setFila(salva || [])}>Desfazer</button>
          <button type="button" className="ng-btn prim" disabled={!mudou || salvando} onClick={salvar}>{salvando ? 'Salvando…' : 'Salvar ordem'}</button>
        </div>
      </div>
      <p className="ng-explic">
        Os pedidos de dashboard novo, alteração e outras solicitações que estão com você na etapa Na fila. Deixe na ordem em que vai atender
        (arraste ou use as setas) e salve: quem pediu recebe um aviso do lugar dele na fila. Cadastro e acesso não entram aqui.
      </p>

      {mudou && <div className="sol-aviso" role="status">Você mudou a ordem e ainda não salvou.</div>}
      {erro && <div className="sol-aviso" role="alert">{erro}</div>}
      {!salva && !erro && <p className="ng-explic">Carregando…</p>}
      {salva && fila.length === 0 && (
        <p className="ng-explic">Sua fila está vazia. Os pedidos aparecem aqui quando a gestão atribui a você e coloca na etapa Na fila.</p>
      )}

      {fila.length > 0 && (
        <ol className="mf-lista" aria-label="Minha fila">
          {fila.map((s, i) => (
            <li key={s.id} className={`kb-card mf-item${s.bloqueado ? ' bloqueado' : ''}${atrasada(s) ? ' atrasado' : ''}${arrastando === s.id ? ' arrastando' : ''}`}
                draggable onDragStart={e => { e.dataTransfer.setData('text/plain', String(s.id)); e.dataTransfer.effectAllowed = 'move'; setArrastando(s.id) }}
                onDragEnd={() => setArrastando(null)} onDragOver={e => e.preventDefault()} onDrop={e => soltar(e, i)}>
              <span className="mf-pos" aria-label={`Lugar ${i + 1}`}>{i + 1}</span>
              <div className="mf-corpo">
                <button type="button" className="kb-abrir" onClick={() => abrir(s.id)} aria-label={`Abrir #${s.id} ${s.titulo}`}>
                  <b>#{s.id} · {s.titulo}</b>
                  {s.ambienteNome && <small className="kb-amb">{s.ambienteNome}</small>}
                  <small>{rotuloTipo(s.tipo)} · {s.solicitanteNome || 'alguém'}</small>
                </button>
                <div className="kb-selos">
                  {s.prioridade !== 'normal' && <span className={`ng-pill ${s.prioridade === 'urgente' ? 'inativo' : s.prioridade === 'alta' ? 'alerta' : ''}`}>{rotuloPrioridade(s.prioridade)}</span>}
                  {s.esforco && <span className="ng-pill" title="Esforço">{s.esforco}</span>}
                  {s.prazo && <span className={`ng-pill${atrasada(s) ? ' inativo' : ''}`}>{atrasada(s) ? 'Atrasada · ' : ''}{dataDoPrazo(s.prazo)}</span>}
                  {s.bloqueado && <span className="ng-pill alerta" title={s.bloqueioMotivo || undefined}>Bloqueada</span>}
                </div>
              </div>
              <div className="mf-botoes">
                <button type="button" className="kb-ico" disabled={i === 0} onClick={() => levar(s.id, 0)} aria-label={`Levar #${s.id} para o topo`} title="Para o topo"><TopoIcon fontSize="small" /></button>
                <button type="button" className="kb-ico" disabled={i === 0} onClick={() => levar(s.id, i - 1)} aria-label={`Subir #${s.id}`} title="Subir"><ArrowUpIcon fontSize="small" /></button>
                <button type="button" className="kb-ico" disabled={i === fila.length - 1} onClick={() => levar(s.id, i + 1)} aria-label={`Descer #${s.id}`} title="Descer"><ArrowDownIcon fontSize="small" /></button>
              </div>
            </li>
          ))}
        </ol>
      )}

      <SolicitacaoDetalhe id={aberta} aoFechar={fechar} />
    </div>
  )
}
